'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Play, ExternalLink, Sparkles, Camera, Film, Scissors, Eye, ArrowUpRight } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';


type Category = 'All' | 'Video Production' | 'On-Site' | 'AI-Enhanced' | 'Editing';

interface Project {
  id: string;
  title: string;
  client: string;
  category: Category;
  tags: string[];
  description: string;
  year: number;
  duration?: string;
  views?: string;
  featured?: boolean;
  image: string;
  imageAlt: string;
  color: string;
}

const projects: Project[] = [
{
  id: 'p1',
  title: 'Meridian Brand Launch',
  client: 'Meridian Capital',
  category: 'Video Production',
  tags: ['Brand Film', 'Cinematic', '4K'],
  description: 'A 3-minute brand anthem film for Meridian\'s Series B launch — shot across 4 cities in 6 days.',
  year: 2025,
  duration: '3:12',
  views: '2.4M',
  featured: true,
  image: "https://images.unsplash.com/photo-1459972046627-196bb66012bb",
  imageAlt: 'Cinematic cityscape at night with golden light streaks, urban brand film setting',
  color: 'from-amber-900/60'
},
{
  id: 'p2',
  title: 'Vaultex Product Reveal',
  client: 'Vaultex Tech',
  category: 'AI-Enhanced',
  tags: ['AI Motion', 'Product', 'CGI'],
  description: 'AI-generated motion graphics merged with live product footage for a hardware launch campaign.',
  year: 2025,
  duration: '1:45',
  views: '890K',
  featured: true,
  image: "https://images.unsplash.com/photo-1656454300703-889c028be15e",
  imageAlt: 'Glowing circuit board with blue and purple light, technology product reveal visual',
  color: 'from-blue-900/60'
},
{
  id: 'p3',
  title: 'Crōss SS26 Campaign',
  client: 'Crōss Apparel',
  category: 'On-Site',
  tags: ['Fashion', 'Editorial', 'On-Location'],
  description: 'On-site editorial shoot across the Atacama Desert — 3 days, 12 looks, zero studio.',
  year: 2025,
  duration: '2:28',
  views: '1.1M',
  image: "https://images.unsplash.com/photo-1725744251346-034053b32ec7",
  imageAlt: 'Fashion model in white minimalist outfit against dramatic desert landscape at golden hour',
  color: 'from-orange-900/60'
},
{
  id: 'p4',
  title: 'Lumena Lifestyle Series',
  client: 'Lumena Wellness',
  category: 'Editing',
  tags: ['Series', 'Color Grade', 'Post-Production'],
  description: '8-part wellness docuseries — full post-production including color grading, sound design, and motion titles.',
  year: 2024,
  duration: '8 eps',
  views: '3.2M',
  image: "https://images.unsplash.com/photo-1699116548123-73affe0987b7",
  imageAlt: 'Serene mountain lake at sunrise with soft pink and golden sky reflection in still water',
  color: 'from-teal-900/60'
},
{
  id: 'p5',
  title: 'Fōrge Industrial Anthem',
  client: 'Fōrge Manufacturing',
  category: 'Video Production',
  tags: ['Industrial', 'Documentary', 'Drone'],
  description: 'Drone-heavy industrial documentary showcasing Fōrge\'s factory operations — for internal and external use.',
  year: 2024,
  duration: '4:55',
  views: '560K',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3618e1f-1765646426340.png",
  imageAlt: 'Aerial drone view of large industrial manufacturing facility with smoke stacks at dusk',
  color: 'from-gray-900/60'
},
{
  id: 'p6',
  title: 'Axiom AI Product Demo',
  client: 'Axiom Labs',
  category: 'AI-Enhanced',
  tags: ['AI Voiceover', 'SaaS', 'Demo'],
  description: 'AI-synthesized voiceover and animated UI walkthroughs for Axiom\'s flagship SaaS product demo.',
  year: 2025,
  duration: '6:30',
  views: '420K',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1223f9fbe-1772107487329.png",
  imageAlt: 'Futuristic holographic data interface with glowing blue neural network visualization',
  color: 'from-indigo-900/60'
},
{
  id: 'p7',
  title: 'Nōble Restaurant Identity',
  client: 'Nōble Group',
  category: 'On-Site',
  tags: ['F&B', 'Brand Identity', 'Photography'],
  description: 'Complete visual identity shoot for 3 restaurant concepts — interiors, food, and lifestyle content.',
  year: 2024,
  duration: '240 assets',
  views: '—',
  image: "https://images.unsplash.com/photo-1668246791341-7d30c2bbb4c3",
  imageAlt: 'Elegant fine dining restaurant interior with warm candlelight, wooden tables and linen napkins',
  color: 'from-rose-900/60'
},
{
  id: 'p8',
  title: 'Drift Automotive Reel',
  client: 'Drift Motors',
  category: 'Video Production',
  tags: ['Automotive', 'High-Speed', '8K'],
  description: '8K automotive reel captured at 240fps — precision high-speed tracking shots on closed circuit.',
  year: 2025,
  duration: '2:00',
  views: '5.7M',
  featured: true,
  image: "https://images.unsplash.com/photo-1681392352593-35037f0d0a09",
  imageAlt: 'Sleek black sports car speeding on wet asphalt with motion blur and dramatic side lighting',
  color: 'from-zinc-900/60'
}];


const categories: Category[] = ['All', 'Video Production', 'On-Site', 'AI-Enhanced', 'Editing'];

const categoryIcons: Record<Category, React.ElementType> = {
  'All': Eye,
  'Video Production': Film,
  'On-Site': Camera,
  'AI-Enhanced': Sparkles,
  'Editing': Scissors
};

function ProjectCard({ project, index }: {project: Project;index: number;}) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const CategoryIcon = categoryIcons[project.category];

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl overflow-hidden bg-studio-900 border border-studio-700/50 card-hover cursor-pointer transition-all duration-500 ${
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`
      }
      style={{ transitionDelay: `${index * 80}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="article"
      aria-label={`${project.title} — ${project.client}`}>
      
      {/* Featured badge */}
      {project.featured &&
      <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 bg-gold-500/90 backdrop-blur-sm rounded-full">
          <span className="text-studio-950 text-[10px] font-bold tracking-[0.1em] uppercase">Featured</span>
        </div>
      }

      {/* Category badge */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 bg-studio-950/80 backdrop-blur-sm border border-studio-600/40 rounded-full">
        <CategoryIcon size={10} className="text-gold-400" />
        <span className="text-studio-200 text-[10px] font-medium">{project.category}</span>
      </div>

      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <AppImage
          src={project.image}
          alt={project.imageAlt}
          fill
          className={`object-cover transition-transform duration-700 ${hovered ? 'scale-105' : 'scale-100'}`}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
        

        {/* Gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t ${project.color} via-transparent to-transparent opacity-70`} />
        <div className={`absolute inset-0 bg-gradient-to-t from-studio-900 via-transparent to-transparent opacity-80`} />

        {/* Play button overlay on hover */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
          hovered ? 'opacity-100' : 'opacity-0'}`
          }>
          
          <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center">
            <Play size={20} className="text-white ml-1" fill="currentColor" />
          </div>
        </div>

        {/* Stats overlay */}
        <div className="absolute bottom-3 left-3 flex items-center gap-3">
          {project.duration &&
          <span className="text-white/80 text-xs font-mono-data bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded">
              {project.duration}
            </span>
          }
          {project.views && project.views !== '—' &&
          <span className="text-white/80 text-xs font-mono-data bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded flex items-center gap-1">
              <Eye size={9} />
              {project.views}
            </span>
          }
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="font-display font-semibold text-base text-white leading-tight mb-0.5">
              {project.title}
            </h3>
            <p className="text-studio-400 text-xs font-medium">{project.client} · {project.year}</p>
          </div>
          <div
            className={`shrink-0 w-8 h-8 rounded-lg bg-studio-800 border border-studio-600/40 flex items-center justify-center transition-all duration-200 ${
            hovered ? 'bg-gold-500/20 border-gold-500/40 text-gold-400' : 'text-studio-400'}`
            }>
            
            <ArrowUpRight size={14} />
          </div>
        </div>

        <p className="text-studio-400 text-sm leading-relaxed mb-4 line-clamp-2">{project.description}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {project.tags.map((tag) =>
          <span
            key={tag}
            className="px-2 py-0.5 bg-studio-800/80 border border-studio-700/40 rounded-md text-studio-300 text-[11px] font-medium">
            
              {tag}
            </span>
          )}
        </div>
      </div>
    </div>);

}

export default function ProjectsGrid() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');

  const filtered = activeCategory === 'All' ?
  projects :
  projects.filter((p) => p.category === activeCategory);

  return (
    <section id="work" className="py-24 xl:py-32 scroll-mt-20" aria-label="Our work">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-gold-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
              Selected Work
            </p>
            <h2 className="font-display font-bold text-4xl md:text-5xl xl:text-6xl text-white leading-tight">
              Projects That
              <br />
              <span className="gradient-text-gold">Define Brands</span>
            </h2>
          </div>
          <p className="text-studio-400 text-base max-w-sm leading-relaxed md:text-right">
            From concept to delivery, every project is crafted with obsessive attention to narrative and visual impact.
          </p>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap items-center gap-2 mb-10" role="tablist" aria-label="Project category filters">
          {categories.map((cat) => {
            const Icon = categoryIcons[cat];
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                role="tab"
                aria-selected={isActive}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gold-500/40 ${
                isActive ?
                'bg-gold-500/15 border border-gold-500/40 text-gold-300' : 'bg-studio-800/60 border border-studio-700/40 text-studio-300 hover:text-white hover:border-studio-600/60 hover:bg-studio-700/60'}`
                }>
                
                <Icon size={13} />
                {cat}
                <span
                  className={`text-xs font-mono-data ml-0.5 ${isActive ? 'text-gold-400' : 'text-studio-500'}`}>
                  
                  {cat === 'All' ? projects.length : projects.filter((p) => p.category === cat).length}
                </span>
              </button>);

          })}
        </div>

        {/* Projects grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5 xl:gap-6">
          {filtered.map((project, i) =>
          <ProjectCard key={project.id} project={project} index={i} />
          )}
        </div>

        {/* View all CTA */}
        <div className="mt-12 text-center">
          <button className="inline-flex items-center gap-2 px-6 py-3 bg-studio-800/60 border border-studio-700/50 text-studio-200 text-sm font-medium rounded-xl hover:text-white hover:border-studio-600/60 hover:bg-studio-700/60 transition-all duration-200 active:scale-95">
            View Full Archive
            <ExternalLink size={14} />
          </button>
        </div>
      </div>
    </section>);

}